/* eslint-disable no-throw-literal */
/* eslint-disable camelcase */
// @flow

import DMPConnect from '../../lib/dmpconnect_lib'
import Utils from './Utils'

const getNestedValue = (obj: any, accessPath: string[]): any =>
    // eslint-disable-next-line implicit-arrow-linebreak
    accessPath.reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj)

const buildResponse = (res: any, fields: Record<string, string | string[] | (() => any)>, rawResponse = false) => {
    const response: Record<string, any> = {}

    Object.keys(fields).forEach((key) => {
        const target = fields[key]

        if (typeof target === 'function') {
            response[key] = target()
        } else {
            const value = Array.isArray(target) ? getNestedValue(res, target) : res[target]
            if (value) response[key] = value
        }
    })

    if (rawResponse) {
        return {
            ...response,
            raw: res,
        }
    }

    return response
}

/**
 *
 * Wrapper for local INSI calls, DMPConnect-JS2 must be installed localy on the user
 * @class INSIConnector
 */
class INSIConnector {
    instance: any

    cpxReaderIndex: any

    cpxReaderName: any

    vitaleReaderIndex: any

    vitaleReaderName: any

    pin: any

    domain: any

    license: any

    sessionId: any

    cpsData: any

    monitoringCpx: any

    monitoringVitale: any

    monitoringCpxCallback: any

    monitoringVitaleCallback: any

    checkingIntervalInSeconds: any

    lpsInfos: any

    minBatchLength: any

    maxBatchLength: any

    insiConnected: any

    /**
     * @description Default constructor
     * @param {String} domain INSI URL
     * @param {String} license dcparameter license
     * @param {Number} checkingIntervalInSeconds monitoring delays
     * @memberof INSIConnector
     */
    constructor(domain: any, license: any, checkingIntervalInSeconds: any, debug = false) {
        const errorManager = (error: any, command: any, timeoutInSeconds: any, answerCallback: any) => {
            answerCallback(error)
        }
        this.instance = new DMPConnect(
            () => {},
            () => {},
            errorManager,
            false,
            '9982',
            'localhost.icanopee.net',
        )
        this.cpxReaderIndex = 1
        this.cpxReaderName = ''
        this.vitaleReaderIndex = 1
        this.vitaleReaderName = ''
        this.pin = ''
        this.domain = domain
        this.license = license
        this.sessionId = ''
        this.monitoringCpx = false
        this.monitoringVitale = false
        this.monitoringCpxCallback = null
        this.monitoringVitaleCallback = null
        this.checkingIntervalInSeconds = checkingIntervalInSeconds
        this.lpsInfos = null
    }

    /**
     * @description set the insi lps params
     * @param {String} authNumber authNumber
     * @param {String} billingNumber billingNumber
     * @param {Object} insParams list of insi configurations, GlobalConfig.insParams
     * @param {String} appVersion version of current software
     * @memberof INSIConnector
     */
    setLpsInfos(authNumber: any, billingNumber: any, insParams: any, appVersion: any) {
        if (!insParams) {
            insParams = {}
        }

        this.minBatchLength = parseInt(insParams.minBatchLength) || 1
        this.maxBatchLength = parseInt(insParams.maxBatchLength) || 100

        this.lpsInfos = {
            s_idam: authNumber,
            s_numAM: insParams.numAM || '04',
            s_version: insParams.version || '1.2.0',
            s_instance: insParams.instance || '85ec351e-9e31-4137-b911-e87029adf7ca',
            s_name: `${insParams.name || 'TTD - ESPACE ETABLISSEMENT'}:${appVersion}`,
            s_billingNumber: billingNumber,
        }
    }

    /**
     * @async
     * @description You need to start by making a session
     * @returns {Promise}
     * @memberof INSIConnector
     */
    async openSession(): Promise<void | null> {
        if (this.sessionId) {
            const alive = await this.getSessionState()
            if (alive) return null
            this.closedSession()
        }

        return new Promise((resolve, reject) => {
            this.instance.hl_openSessionWithCustomDcParam(this.license, 3600, undefined, (session: any) => {
                if (typeof session.s_sessionId === 'undefined' || session.s_sessionId === '') reject('WS_DOWN')
                else {
                    this.sessionId = session.s_sessionId
                    resolve()
                }
            })
        })
    }

    /**
     * @async
     * @private
     * @description check if session is alive
     * @returns Promise of true or false
     */
    async getSessionState() {
        return new Promise((resolve) => {
            this.instance.hl_getSessionState({}, (result: any) => {
                if (typeof result.s_status === 'undefined' || result.s_status === 'ERROR') return resolve(false)
                return resolve(true)
            })
        })
    }

    /**
     * @private
     * @description reset session
     */
    closedSession() {
        this.sessionId = ''
        this.insiConnected = false
        this.monitoringCpx = false
        this.monitoringVitale = false
    }

    /**
     * @async
     * @description  can be beter to close the session then but they are anyway flushed
     * @returns {Promise}
     * @memberof INSIConnector
     */
    async closeSession(): Promise<void | null> {
        if (!this.sessionId) return null
        this.closedSession()
        return new Promise((resolve) => {
            this.instance.hl_closeSession(() => resolve())
        })
    }

    /**
     * @async
     * @description return the list of physical card readers
     * @returns {Promise<array>} of {
     *   name: physical name of reader,
     *   cardInside: 1 => Empty, 2 => Vitale card, 3 => CPS card
     * }
     * @memberof INSIConnector
     */
    async getCardReaders() {
        return new Promise((resolve) => {
            this.instance.hl_getPcscReaders((result: any) => {
                if (!result.Readers) {
                    return resolve([])
                }
                const res = result.Readers.map((reader: any, index: any) => {
                    let cardType = ''
                    switch (reader.i_slotType) {
                        case 1:
                            cardType = 'aucune carte'
                            break
                        case 2:
                            cardType = 'carte vitale'
                            break
                        case 3:
                            cardType = 'carte cps'
                            break
                        case 4:
                            cardType = 'carte inconnue'
                            break
                        case 5:
                            cardType = 'détection impossible'
                            break
                        default:
                            break
                    }
                    return {
                        cardInside: reader.i_slotType,
                        name: `${reader.s_name} (${cardType})`,
                        s_name: reader.s_name,
                        index,
                    }
                })
                return resolve(res)
            })
        })
    }

    /**
     * @async
     * @private
     * @description private, hl_getCpxCard wrapper to counter callback hell
     * @param {Number} index which slot for multislot reader, if not, 1
     * @param {String} name name of the reader, from getCardReaders
     * @memberof INSIConnector
     */
    getCpsReader(index: number, name: string): Promise<void> {
        return new Promise((resolve, reject) => {
            if (this.monitoringCpx === false) {
                this.instance.hl_getCpxCard(index, name, (cpxRes: any) => {
                    if (cpxRes.s_status === 'OK') {
                        this.monitoringCpx = true
                        this.instance.hl_startCpxCardMonitoring(this.checkingIntervalInSeconds, (res: any) => {
                            if (typeof res.i_cardStatus === 'undefined') return
                            if (res.i_cardStatus !== 1) {
                                if (this.monitoringCpxCallback) this.monitoringCpxCallback()
                                this.closeSession()
                            }
                        })
                        return resolve()
                    }
                    return reject(cpxRes)
                })
            }
            resolve()
        })
    }

    /**
     * @async
     * @description define which reader will be for CPS carefull, there is no error management
     * @param {Number} index which slot for multislot reader, if not, 1
     * @param {String} name name of the reader, from getCardReaders
     * @memberof INSIConnector
     */
    async setCpsReader(index: number, name: string) {
        await this.getCpsReader(index, name)
        this.cpxReaderIndex = index
        this.cpxReaderName = name
    }

    /**
     * @async
     * @description private, hl_getCpxStatus wrapper to counter callback hell
     * @memberof INSIConnector
     */
    getCpsStatus() {
        return new Promise((resolve, reject) => {
            this.instance.hl_getCpxStatus((res: any) => {
                if (res.s_status === 'OK') return resolve(res.i_cardStatus) // i_cardStatus = 1 when status is ok
                return reject(res)
            })
        })
    }

    /**
     * @async
     * @description read the data on the CPS card, to enable decryption call setCpsReader first
     * @param {String} pin code pin of the CPS card
     * @returns {Promise}
     * @memberof INSIConnector
     */
    async readCPxCard(pin: string): Promise<void> {
        if (!pin) throw new Error('PIN_REQUIRED')
        if (!this.cpxReaderName) throw new Error('NO_CPS_READER')
        await this.getCpsReader(this.cpxReaderIndex, this.cpxReaderName)
        return new Promise((resolve, reject) => {
            this.instance.hl_readCpxCard(pin, (result: any) => {
                if (result.s_status === 'OK') {
                    this.cpsData = result
                    this.pin = pin
                    return resolve()
                }
                let error = 'WS_DOWN'
                if (result.i_apiErrorCode === 6) error = 'NO_CPS_READER'
                else if (result.i_apiErrorCode === 7) error = 'WRONG_PIN'
                else if (result.i_apiErrorCode === 8) error = 'CARD_BLOCKED'
                else if (result.i_apiErrorCode === 9) error = 'NO_CARD'
                return reject(error)
            })
        })
    }

    /**
     * @async
     * @description private, hl_getVitaleCard wrapper to counter callback hell
     * @param {Number} index which slot for multislot reader, if not, 1
     * @param {String} name name of the reader, from getCardReaders
     * @memberof INSIConnector
     */
    getVitaleReader(index: number, name: string): Promise<void> | false {
        if (this.monitoringVitale === false) {
            return new Promise((resolve, reject) => {
                this.instance.hl_getVitaleCard(index, name, (vitalRes: any) => {
                    if (vitalRes.s_status === 'OK') {
                        this.monitoringVitale = true
                        this.instance.hl_startVitaleCardMonitoring(this.checkingIntervalInSeconds, (res: any) => {
                            if (typeof res.i_cardStatus === 'undefined') return
                            if (res.i_cardStatus !== 2) {
                                this.monitoringVitaleCallback()
                            }
                        })
                        return resolve()
                    }
                    return reject(vitalRes)
                })
            })
        }
        return false
    }

    /**
     * @async
     * @description define which reader will be for Vitale carefull, there is no error management
     * @param {Number} index which slot for multislot reader, if not, 1
     * @param {String} name name of the reader, from getCardReaders
     * @param {Function} callback callback called by monitoring if the card is removed
     * @memberof INSIConnector
     */
    async setVitaleReader(index: number, name: string, callback: Function) {
        this.monitoringVitaleCallback = callback
        await this.getVitaleReader(index, name)
        this.vitaleReaderIndex = index
        this.vitaleReaderName = name
    }

    /**
     * @async
     * @description read the data on the Vitale card, you need to have read a CPS before call setCpsReader first
     * @returns {Promise<Array<Object>>} data on the card
     * @memberof INSIConnector
     */
    async readVitaleCard() {
        if (!this.vitaleReaderName) throw new Error('NO_VITALE_READER')
        return new Promise((resolve, reject) => {
            this.instance.hl_readVitaleCard((result: any) => {
                if (result.s_status === 'OK' && result.Patients) {
                    const res = result.Patients.map((patient: any, index: any) => ({
                        firstName: patient.s_given,
                        lastName: patient.s_name,
                        birthName: patient.s_birthname,
                        birthday: patient.s_birthdayYYYYMMDD.replace(/(\d{4})(\d{2})(\d{2})/g, '$1-$2-$3'),
                        nir: patient.s_certifiedNir
                            ? patient.s_certifiedNir.replaceAll(' ', '')
                            : patient.s_nir.replaceAll(' ', ''),
                        ins: patient.s_insC,
                        index,
                    }))
                    const obj = {
                        statusCard: result.i_cardStatus,
                        data: res,
                    }
                    return resolve(obj)
                }

                let error = 'WS_DOWN'
                if (result.i_apiErrorCode === 6) error = 'NO_VITALE_READER'
                if (result.i_apiErrorCode === 9) error = 'NO_CARD'
                if (result.i_apiErrorCode === 15) error = 'WRONG_CARD'
                if (result.i_apiErrorCode === 18) error = 'ERR_DATA'
                return reject(error)
            })
        })
    }

    /**
     * @async
     * @description private, connect to INSI server, you must have read CPS Data
     * @returns {Promise}
     * @memberof INSIConnector
     */
    insiConnection(): Promise<void> {
        return new Promise((resolve) => {
            this.instance.hl_createInsiConnector(this.pin, this.domain, 60, 'AMBULATOIRE', 0, null, (res: any) => {
                if (typeof res.i_apiErrorCode !== 'undefined') throw res
                this.insiConnected = true
                return resolve()
            })
        })
    }

    /**
     * @async
     * @description Search if a patient realy exists with INSi - ws_Ins2
     * @param {String} lastName
     * @param {String} firstName
     * @param {Number} sex
     * @param {String} birthday YYYY-MM-DD
     * @param {String} birthPlace optional, postal code
     * @memberof INSIConnector
     */
    async searchPatient(
        lastName: string,
        firstName: string,
        sex: number,
        birthday: string,
        birthPlace: string,
        rawResponse = false,
    ) {
        if (!this.insiConnected) await this.insiConnection()
        return new Promise((resolve) => {
            this.instance.hl_getInsFromIdentityInformation(
                this.lpsInfos,
                lastName,
                firstName,
                sex,
                birthday,
                birthPlace,
                (res: any) => {
                    if (res.s_status === 'OK') {
                        if (res.i_insIdentityResult === 2) {
                            return resolve(
                                buildResponse(
                                    res,
                                    {
                                        firstName: ['Identity', 's_given'],
                                        firstNames: ['Identity', 's_birthGiven'],
                                        lastName: ['Identity', 's_birthName'],
                                        birthName: ['Identity', 's_birthName'],
                                        birthday: ['Identity', 's_birthDate'],
                                        birthPlace: ['Identity', 's_birthPlace'],
                                        sex: ['Identity', 'i_sex'],
                                        ins: ['Identity', 'Ins', 's_value'],
                                        ins_oid: ['Identity', 'Ins', 's_oid'],
                                        ins_key: ['Identity', 'Ins', 's_key'],
                                        identityResult: ['i_insIdentityResult'],
                                        history: ['Identity', 'History'],
                                    },
                                    rawResponse,
                                ),
                            )
                        }

                        return resolve(
                            buildResponse(
                                res,
                                {
                                    identityResult: 'i_insIdentityResult',
                                },
                                rawResponse,
                            ),
                        )
                    }

                    return resolve(
                        buildResponse(
                            res,
                            {
                                identityResult: () => 1, // this is error code
                            },
                            rawResponse,
                        ),
                    )
                },
            )
        })
    }

    /**
     * @async
     * @description Search if a patient realy exists with INSi - ws_Ins1
     * @param {Number} index
     * @returns {Promise}
     * @memberof INSIConnector
     */
    async searchPatientWithVitaleCardIndex(index: number, rawResponse = false) {
        if (!this.insiConnected) await this.insiConnection()
        return new Promise((resolve, reject) => {
            this.instance.hl_getInsFromVitaleCard(this.lpsInfos, index, (res: any) => {
                if (res.s_status === 'OK') {
                    if (res.i_insIdentityResult === 2) {
                        return resolve(
                            buildResponse(
                                res,
                                {
                                    firstName: ['Identity', 's_given'],
                                    firstNames: ['Identity', 's_birthGiven'],
                                    lastName: ['Identity', 's_birthName'],
                                    birthName: ['Identity', 's_birthName'],
                                    birthday: ['Identity', 's_birthDate'],
                                    birthPlace: ['Identity', 's_birthPlace'],
                                    sex: ['Identity', 'i_sex'],
                                    ins: ['Identity', 'Ins', 's_value'],
                                    ins_oid: ['Identity', 'Ins', 's_oid'],
                                    ins_key: ['Identity', 'Ins', 's_key'],
                                    history: ['Identity', 'History'],
                                },
                                rawResponse,
                            ),
                        )
                    }
                }
                return reject(res.i_insIdentityResult || 'NOT_FOUND')
            })
        })
    }

    /**
     * @async
     * @description Confirm an INSI identity
     * @param {String} ins
     * @param {String} key
     * @param {String} oid
     * @param {String} lastName
     * @param {String} firstNames
     * @param {String} sex
     * @param {String} birtdate YYYY-MM-DD
     * @param {String} birthPlace optional, postal code
     * @returns {Promise}
     * @memberof INSIConnector
     */
    async checkPatient(
        ins: string,
        key: string,
        oid: string,
        lastName: string,
        firstNames: string,
        sex: string,
        birthday: string,
        birthPlace: string,
        rawResponse = false,
    ) {
        if (!this.insiConnected) await this.insiConnection()
        return new Promise((resolve, reject) => {
            this.instance.hl_checkInsIdentity(
                this.lpsInfos,
                ins,
                key,
                oid,
                lastName,
                firstNames,
                sex,
                birthday,
                birthPlace,
                (res: any) => {
                    if (rawResponse) {
                        return resolve(res)
                    }

                    if (res.s_status === 'OK') {
                        return resolve(res)
                    }
                    if (res.i_apiErrorCode === 3) return resolve(false)

                    return reject(new Error('ERROR'))
                },
            )
        })
    }

    /**
     * @async
     * @description create batches to verify
     * @param {Array} rawPatients list of patients patients
     * @returns the list of batch, expected delay in s and the list of patient in order
     */
    async sendBatches(rawPatients: any) {
        if (!this.insiConnected) await this.insiConnection()
        const failed: any = []

        let patients = rawPatients.map((patient: any) => {
            let formatSex = 1
            let lastName = patient.birthName
            let firstNames = patient.firstNameList

            try {
                if (!lastName || !firstNames || !patient.insNum || !patient.birthDate || !patient.birthDate.iso) {
                    throw 'MISSING_BASE'
                }
                if (!patient.insOid) return null

                lastName = lastName.trim().toUpperCase().normalize('NFD')
                firstNames = firstNames ? firstNames.map((el: any) => el.trim().toUpperCase().normalize('NFD')) : []

                if (patient.sex.toUpperCase() === 'M') formatSex = 2
                else if (patient.sex.toUpperCase() === 'F') formatSex = 3

                if (lastName.length === 0 || firstNames.length === 0 || !firstNames[0] || firstNames[0].length === 0) {
                    throw 'MISSING_BASE'
                }
                if (patient.insNum.length !== 15) throw 'WRONG_INS'

                if (Utils.getAge(patient.birthDate.iso) > 100) throw 'WRONG_AGE'
                if (patient.isDemo) throw false

                firstNames.forEach((firstName: any) => {
                    if (/[\u0300-\u036f\u00C0-\u024F[\]0-9]/g.test(firstName)) throw 'INCORRECT_FIRSTNAME'
                    if ([' ', '-', "'"].includes(firstName[0])) throw 'INCORRECT_FIRSTNAME'
                })

                if (/[\u0300-\u036f\u00C0-\u024F[\]0_9]/g.test(lastName)) throw 'INCORRECT_NAME'
                if ([' ', '-', "'"].includes(lastName[0])) throw 'INCORRECT_NAME'
                const firstName = firstNames[0]
                firstNames = firstNames.join(' ')

                return {
                    objectId: patient.objectId,
                    s_ins: patient.insNum.substring(0, 13),
                    s_key: patient.insNum.substring(13, 15),
                    s_oid: patient.insOid,
                    s_birthName: lastName,
                    s_given: firstName,
                    s_birthGiven: firstNames,
                    i_sex: formatSex,
                    s_birthDate: Utils.formatBirthDateToTen(patient.birthDate.iso),
                    s_birthPlace: patient.birthCityCode || '',
                }
            } catch (e) {
                if (e) {
                    failed.push({
                        name: `${lastName} ${firstNames}`,
                        error: e,
                    })
                }
                return null
            }
        })

        patients = patients.filter((patient: any) => patient !== null)

        if (this.maxBatchLength !== 100 && patients.length > this.maxBatchLength) throw new Error('TOO_MANY_PATIENTS')
        if (this.minBatchLength !== 1 && patients.length < this.minBatchLength) throw new Error('TOO_FEW_PATIENTS')
        if (patients.length === 0) throw new Error('TOO_FEW_PATIENTS')

        const batches: any = []

        let { maxBatchLength } = this
        if (maxBatchLength > 100) maxBatchLength = 100

        while (patients.length >= this.minBatchLength) {
            const creatingBatch = patients.splice(0, maxBatchLength)
            const submitedInsNums: any = []

            creatingBatch.forEach((patient: any) => {
                submitedInsNums.push(patient.objectId)
                delete patient.objectId
            })

            batches.push({
                data: creatingBatch,
                submitedInsNums,
            })
        }

        const promises: any[] = []

        for (const currentBatch of batches) {
            promises.push(
                new Promise((resolve, reject) => {
                    this.instance.hl_submitInsiBatchValidationSet(this.lpsInfos, currentBatch.data, (res: any) => {
                        if (res.s_status === 'OK') {
                            resolve({
                                batchId: res.s_batchId,
                                estimationDelay: res.i_estimationDelay,
                                submitedInsNums: currentBatch.submitedInsNums,
                            })
                        } else {
                            reject('ERR_BATCH')
                        }
                    })
                }),
            )
        }

        const res = await Promise.all(promises)

        return {
            failed,
            batches: res,
        }
    }

    /**
     * @async
     * @description check batches result and send a readable set
     * @param {Array<String>} batchesId list of batches
     * @param {Array<Object>} submitedInsNums list of patients in the same order than the request
     * @return {Promise<Array>} Array of result per batch
     */
    async checkBatches(batchesId: any, submitedInsNums: any) {
        if (!this.insiConnected) await this.insiConnection()

        const promises: any[] = []

        for (const batch of batchesId) {
            promises.push(
                new Promise((resolve) => {
                    this.instance.hl_getInsiBatchValidationSetResult(this.lpsInfos, batch, (res: any) => {
                        try {
                            if (res.s_status === 'OK') {
                                if (res.i_batchValidationResult === 3) {
                                    resolve({
                                        delayed: res.i_estimationDelay,
                                    })
                                }
                                if (res.i_batchValidationResult === 4) resolve(4)

                                const result: any = []

                                res.Result.forEach((patient: any, index: any) => {
                                    if (patient.i_validationResult === 1) {
                                        result.push({
                                            objectId: submitedInsNums[batch][index],
                                            ins: `${patient.s_ins}${patient.s_key}`,
                                            oid: patient.s_oid,
                                        })
                                    } else {
                                        result.push({
                                            objectId: submitedInsNums[batch][index],
                                            oid: false,
                                        })
                                    }
                                })
                                resolve(result)
                            } else {
                                resolve(4)
                            }
                        } catch (e) {
                            resolve(4)
                        }
                    })
                }),
            )
        }

        return Promise.all(promises)
    }

    /**
     * @async
     * @description Check if the service is available by trying to open a session
     * @returns {Boolean}
     * @memberof INSIConnector
     */
    async checkDmpConnectJsServiceAvailable() {
        try {
            await this.openSession()
            return true
        } catch (e) {
            return false
        }
    }
}

export default INSIConnector
